<template>
  <div class="live-map-page">
    <div class="live-head">
      <div>
        <h4>{{ $t('fleetMaps.live_tracking_title') }}</h4>
        <span class="muted">{{ $t('fleetMaps.running_and_refresh', {count: trucks.length, sec: intervalSec}) }}</span>
      </div>
      <button class="reload-btn" :disabled="loading" @click="fetchLive">
        <i class="fa fa-sync"></i> {{ $t('fleetMaps.reload') }}
      </button>
    </div>

    <div class="live-body">
      <div class="live-list">
        <div class="list-search">
          <i class="fal fa-search"></i>
          <input v-model="search" type="text" :placeholder="$t('fleetMaps.search_placeholder')">
          <i v-if="search" class="fal fa-times clear" :title="$t('fleetMaps.clear')" @click="search = ''"></i>
        </div>
        <div v-if="!filteredTrucks.length" class="empty">
          {{ search ? $t('fleetMaps.no_search_results') : $t('fleetMaps.no_trucks_running') }}
        </div>
        <div
            v-for="t in filteredTrucks"
            :key="t.trip_id"
            class="truck-row"
            :class="{stale: t.stale, picked: selected && selected.trip_id === t.trip_id}"
            :title="selected && selected.trip_id === t.trip_id ? $t('fleetMaps.click_again_show_all') : $t('fleetMaps.click_show_only_this')"
            @click="toggleSelect(t)"
        >
          <div class="row-top">
            <span class="plate">{{ t.truck_plate || '—' }}</span>
            <span v-if="t.direction" class="dir-chip" :class="dirClass(t)">
              <i :class="dirIcon(t)"></i>
              {{ dirLabel(t) }}
            </span>
            <span class="badge" :class="{stale: t.stale}">{{ t.stale ? $t('fleetMaps.stale') : $t('fleetMaps.live') }}</span>
          </div>
          <div class="meta">{{ t.route_name || '—' }} · {{ $t('fleetMaps.round_n', {n: t.round_number}) }} · {{ t.trip_code }}</div>

          <div class="manifest">
            <span class="m in"><b>+{{ mf(t).received }}</b> {{ $t('fleetMaps.received_total') }}</span>
            <span class="m out"><b>−{{ mf(t).unloaded }}</b> {{ $t('fleetMaps.unloaded_total') }}</span>
            <span class="m board"><b>{{ mf(t).on_board }}</b> {{ $t('fleetMaps.on_board_total') }}</span>
          </div>
          <div v-if="mf(t).off_route" class="offroute" :title="offRouteHint">
            {{ $t('fleetMaps.of_which') }} <b>{{ mf(t).off_route }}</b> {{ $t('fleetMaps.off_route') }}
            <template v-if="mf(t).off_route_on_board">{{ $t('fleetMaps.off_route_on_board', {n: mf(t).off_route_on_board}) }}</template>
          </div>

          <div class="row-bottom">
            <span class="seen">{{ $t('fleetMaps.last_seen', {time: lastSeen(t)}) }}</span>
            <button class="detail-btn" :title="$t('fleetMaps.open_trip_detail_page')" @click.stop="goToTrip(t)">
              <i class="fal fa-list-alt"></i> {{ $t('fleetMaps.trip_detail') }}
            </button>
          </div>
        </div>
      </div>

      <div class="live-map">
        <div class="map-status">
          <span v-if="selected" class="ms-pick">
            <i class="fal fa-crosshairs"></i>
            {{ $t('fleetMaps.following') }} <b>{{ selected.truck_plate || '—' }}</b>
            <template v-if="hiddenBySelection">· {{ $t('fleetMaps.hidden_count', {n: hiddenBySelection}) }}</template>
            <button class="ms-clear" @click="clearSelection">{{ $t('fleetMaps.show_all_trucks') }}</button>
          </span>
          <span v-else class="ms-all">
            <i class="fal fa-signal-stream"></i>
            {{ $t('fleetMaps.showing') }} <b>{{ activeCount }}</b> {{ $t('fleetMaps.trucks_started_trip') }}
            <template v-if="staleCount">· {{ $t('fleetMaps.stale_count_note', {n: staleCount}) }}</template>
          </span>
        </div>
        <GmapMap
            ref="map"
            :center="center"
            :zoom="zoom"
            :options="mapOptions"
            :map-type-id="mapTypeId"
            style="width: 100%; height: 540px;"
            @zoom_changed="onZoomChanged"
        >
          <GmapPolyline
              v-for="line in routeLines"
              :key="'line-' + line.trip_id"
              :path="line.path"
              :options="line.options"
          />
          <GmapMarker
              v-for="s in stopPoints"
              :key="'stop-' + s.key"
              :position="{lat: s.lat, lng: s.lng}"
              :icon="stopIcon(s)"
              :title="s.title"
              :label="s.label"
              :z-index="1"
          />
          <GmapMarker
              v-for="t in mappable"
              :key="'truck-' + t.trip_id"
              :position="truckPos(t)"
              :icon="truckIcon(t)"
              :label="truckLabel(t)"
              :title="truckTitle(t)"
              :z-index="10"
              @click="select(t)"
          />
          <!-- Bound to infoTrip, NOT selected: closing the bubble must only
               close the bubble. While they shared one field, the X released
               the follow too and the map re-framed the whole fleet. -->
          <GmapInfoWindow
              v-if="infoTrip"
              :position="truckPos(infoTrip)"
              :opened="true"
              @closeclick="infoTrip = null"
          >
            <div class="truck-info">
              <div class="ti-head">
                <span class="ti-plate">{{ infoTrip.truck_plate || '—' }}</span>
                <span v-if="infoTrip.direction" class="ti-dir" :class="dirClass(infoTrip)">{{ dirLabel(infoTrip) }}</span>
              </div>
              <div class="ti-meta">{{ infoTrip.route_name || '—' }} · {{ $t('fleetMaps.round_n', {n: infoTrip.round_number}) }}</div>
              <div class="ti-lines">
                <span><b class="in">+{{ mf(infoTrip).received }}</b> {{ $t('fleetMaps.received_total') }}</span>
                <span><b class="out">−{{ mf(infoTrip).unloaded }}</b> {{ $t('fleetMaps.unloaded_total') }}</span>
                <span><b>{{ mf(infoTrip).on_board }}</b> {{ $t('fleetMaps.on_board_total') }}</span>
              </div>
              <div v-if="mf(infoTrip).off_route" class="ti-offroute" :title="offRouteHint">
                {{ $t('fleetMaps.of_which') }} <b>{{ mf(infoTrip).off_route }}</b> {{ $t('fleetMaps.off_route') }}
                <template v-if="mf(infoTrip).off_route_on_board">{{ $t('fleetMaps.off_route_on_board', {n: mf(infoTrip).off_route_on_board}) }}</template>
              </div>
              <div class="ti-seen">{{ $t('fleetMaps.last_seen', {time: lastSeen(infoTrip)}) }}</div>
              <button class="ti-detail-btn" @click="goToTrip(infoTrip)">
                <i class="fal fa-list-alt"></i> {{ $t('fleetMaps.open_trip_detail') }}
              </button>
            </div>
          </GmapInfoWindow>
        </GmapMap>
        <button class="map-mode-btn" :title="$t('fleetMaps.change_map_view')" @click="cycleMapMode">
          <i :class="currentMapMode.icon"></i> {{ currentMapMode.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import FleetHelpers, {fleetTruckIcon, fleetTruckLabel, fleetPinIcon, fleetRoadPath, fleetDirection, FLEET_BASE, FLEET_MAP_MODES} from '@/Mixins/Fleet/FleetHelpers';

/**
 * US2 (001-fleet-gps-tracking): live truck positions for dispatch. Polls
 * GET /v1/fleet/reports/live-positions on a short interval and draws EVERY
 * truck on a started trip as a TRUCK marker — red when reporting, grey when
 * contact has dropped. Each row shows the leg direction (ຂາໄປ/ຂາກັບ) + a
 * WHOLE-TRUCK manifest summary. Self-hides gracefully on an old backend
 * (404/empty). No history — ephemeral operational view only.
 *
 * The map also draws each trip's ORDERED STOP POINTS (numbered pins: green =
 * the stop the truck is heading for, grey = departed/skipped, blue = ahead)
 * joined by the ROAD-following line, same as the route builder — via the
 * browser Directions service (fleetRoadPath), NOT the server's stored
 * route_geometry, which needs FLEET_MAPS_API_KEY and is currently unset.
 * Straight segments show until Directions answers, and permanently if it
 * cannot. The geometry is cached PER TRIP, keyed on its stop coordinates, so
 * the poll never re-asks for a route whose stops have not changed — that
 * caching is what makes road lines affordable on a view that refreshes every
 * few seconds.
 *
 * Show-all by default, FOLLOW on selection: clicking a LIST ROW (or the truck's
 * marker) leaves only that truck and its stops on the map, zooms in far enough
 * that its movement is actually visible, and keeps it centred as it drives. A
 * chip brings the rest back. Each list row carries its own ລາຍລະອຽດຖ້ຽວ button
 * through to the trip detail — the row itself selects, so navigation needs its
 * own control.
 *
 * Markers GLIDE between fixes rather than teleporting. A ping lands every ~5s
 * (simulator) to ~50s (real device), and setting the position outright made the
 * truck jump, which reads as a broken map rather than a driving truck. Each
 * poll retargets an eased tween from wherever the marker actually is, so a fix
 * arriving mid-glide continues smoothly instead of stuttering backwards. Only
 * the DRAWN position is interpolated — every value shown in the list, the info
 * window and the ETA is the real reported one. A jump beyond animMaxGlideKm
 * (first fix, trip switch, GPS spike) snaps instead of sliding across country.
 *
 * The map must never fight the operator. A routine poll NEVER re-fits: fitting
 * happens only when the set of visible trucks changes. Re-fitting every 12s
 * pinned the view to the whole route (VTE→Bokeo is ~600 km), where a truck at
 * 60 km/h advances well under a pixel per refresh and looks parked — and it
 * stole the zoom back the instant anyone zoomed in to watch. Manual zoom is
 * mirrored back into state via @zoom_changed so nothing re-applies a stale one.
 *
 * A stale truck keeps its pin, in grey. Hiding them was tried and reverted: a
 * truck whose signal dropped is precisely the one dispatch needs to locate, and
 * on a small fleet it emptied the map entirely. Colour carries "reporting or
 * not" without taking the truck off the map.
 * `stale` comes from the backend: seconds_since > fleet.gps.stale_threshold_s.
 */
export default {
  name: 'FleetLiveMap',
  mixins: [FleetHelpers],
  data() {
    return {
      // These three totals are WHOLE-TRUCK figures: unlike the trip header and
      // the stop-load ledger (which carve off-route out), they INCLUDE off-route
      // pieces, because a dispatcher watching a moving truck needs the physical
      // load. Hence the ລວມ suffix + the "ໃນນັ້ນ" phrasing on the off-route line,
      // which is a SUBSET here, not an addition.
      offRouteHint: this.$t('fleetMaps.off_route_hint'),
      trucks: [],
      search: '',
      // Which truck the map narrows to and follows.
      selected: null,
      // Which truck's info bubble is open. Deliberately SEPARATE from `selected`
      // — dismissing a popup should not change what the map is doing.
      infoTrip: null,
      loading: false,
      timer: null,
      intervalSec: 12,
      center: {lat: 18.1189434, lng: 102.290218},
      mapTypeId: 'roadmap',
      mapModes: FLEET_MAP_MODES,
      mapOptions: {
        zoomControl: true,
        scaleControl: true,
        fullscreenControl: true,
        streetViewControl: false,
        mapTypeControl: false,
      },
      // Rotated truck icons, memoised by colour + heading bucket. Rebuilding the
      // data-URI on every poll would hand Google a brand-new icon object each
      // refresh and make the marker flicker; 5-degree buckets are finer than the
      // eye can read and cap the cache at ~72 entries per colour.
      truckIcons: {},
      // tripId => last non-null heading, so a stationary fix (which often reports
      // none) keeps the truck pointing where it was last going.
      lastHeadings: {},
      // Smooth motion between fixes. A ping lands every ~5s (simulator) to ~50s
      // (real device); without this the marker teleports on each poll, which
      // reads as broken rather than as a truck driving.
      animPos: {},        // tripId => the position we actually DRAW
      tweens: {},         // tripId => {from, to, start} in flight
      animRaf: null,
      animDurationMs: 1200,
      // Beyond this, snap instead of gliding. A first fix, a trip switch or a
      // GPS jump would otherwise send the truck sliding across the country.
      animMaxGlideKm: 5,
      nextStopIcon: fleetPinIcon('#2dcc70'),
      aheadStopIcon: fleetPinIcon('#195BFF'),
      doneStopIcon: fleetPinIcon('#9aa0a6'),
      // tripId => {key, path} — Directions geometry cached per trip so the
      // 12-second poll never re-asks for a route whose stops have not changed.
      roadPaths: {},
      zoom: 7,
      // Which set of trucks the view was last fitted to, so a routine poll does
      // not re-fit (and re-steal the zoom).
      fittedSignature: '',
      // Zoom used when following one truck. At the whole-route zoom a truck doing
      // 60 km/h moves ~1.7 km per refresh, which is invisible; this is close
      // enough that the movement actually reads.
      followZoom: 14,
    };
  },
  computed: {
    currentMapMode() {
      return this.mapModes.find((mode) => mode.id === this.mapTypeId) || this.mapModes[0];
    },
    // Client-side filter over trip code / route name / truck plate.
    filteredTrucks() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.trucks;
      return this.trucks.filter((t) => `${t.trip_code || ''} ${t.route_name || ''} ${t.truck_plate || ''}`
          .toLowerCase().includes(q));
    },
    // Everything the search matched that actually has coordinates.
    positioned() {
      return this.filteredTrucks
          .filter((t) => Number(t.lat) && Number(t.lng))
          .map((t) => ({...t, lat: Number(t.lat), lng: Number(t.lng)}));
    },
    // What gets a pin: EVERY truck on a started trip, so the map is the fleet.
    // Selecting one narrows to just that truck; clearing brings them all back.
    //
    // Stale trucks are pinned too, in grey (see truckIcon). They were hidden
    // here briefly and that was wrong: a truck whose signal dropped is exactly
    // the one dispatch needs to find, and with a small fleet it emptied the map
    // completely. Grey vs red carries "reporting or not" without removing it.
    mappable() {
      if (this.selected) {
        const one = this.positioned.find((t) => t.trip_id === this.selected.trip_id);
        return one ? [one] : [];
      }
      return this.positioned;
    },
    // Trucks on the map when nothing is selected.
    activeCount() {
      return this.positioned.length;
    },
    // How many the map is holding back because one truck is selected.
    hiddenBySelection() {
      return this.selected ? Math.max(this.activeCount - this.mappable.length, 0) : 0;
    },
    // Of the pinned trucks, how many have stopped reporting (grey pins).
    staleCount() {
      return this.positioned.filter((t) => t.stale).length;
    },
    // Every stop point of every truck currently on the map. Facilities with no
    // pinned coordinates come back as 0/0 from the backend and are dropped —
    // a DC ships with lat/lng 0 until someone drops its pin in the route
    // builder, and 0,0 is the Gulf of Guinea, not a stop.
    stopPoints() {
      const out = [];
      this.mappable.forEach((t) => {
        (t.stops || []).forEach((s) => {
          const lat = Number(s.lat);
          const lng = Number(s.lng);
          if (!lat || !lng) return;
          out.push({
            key: `${t.trip_id}-${s.id}`,
            lat,
            lng,
            sequence: s.sequence,
            is_next: !!s.is_next,
            status: s.status,
            label: {text: String(s.sequence), color: 'white', fontSize: '11px', fontWeight: 'bold'},
            title: `${s.sequence}. ${s.name || '—'}${s.is_next ? ` · ${this.$t('fleetMaps.next_stop')}` : ''}`,
          });
        });
      });

      return out;
    },
    // The ordered stop coordinates of each visible trip — the straight skeleton
    // of the run, and the input the road lookup is keyed on.
    stopPathsByTrip() {
      const out = {};
      this.mappable.forEach((t) => {
        const path = (t.stops || [])
            .filter((s) => Number(s.lat) && Number(s.lng))
            .map((s) => ({lat: Number(s.lat), lng: Number(s.lng)}));
        if (path.length > 1) out[t.trip_id] = path;
      });

      return out;
    },
    // Road-following line per trip, exactly like the route builder — falling
    // back to straight segments until Directions answers, and permanently if it
    // cannot (API not enabled, quota, offline).
    //
    // The earlier straight-only version was wrong: every other fleet map draws
    // roads, so this one read as a different product. The quota worry behind it
    // was real but the fix is caching, not straight lines — a trip's stops do
    // not change between polls, so the road geometry is fetched ONCE per trip
    // (keyed on its stop coordinates) and reused across every refresh.
    routeLines() {
      return Object.keys(this.stopPathsByTrip).map((tripId) => {
        const straight = this.stopPathsByTrip[tripId];
        const cached = this.roadPaths[tripId];
        const truck = this.mappable.find((t) => String(t.trip_id) === String(tripId));

        return {
          trip_id: tripId,
          path: (cached && cached.key === JSON.stringify(straight)) ? cached.path : straight,
          options: {
            strokeColor: (truck && truck.stale) ? '#9aa0a6' : '#195BFF',
            strokeOpacity: 0.55,
            strokeWeight: 3,
          },
        };
      });
    },
  },
  watch: {
    // Re-fit the map to the matches as the search narrows.
    search() {
      this.fitToTrucks();
    },
    // Clearing the selection re-frames the whole fleet. Selecting does NOT
    // fit — select() centres and zooms in to follow that truck instead.
    selected(t) {
      if (!t) this.fitToTrucks();
    },
    // Ask Directions for any trip whose stop set we have not resolved yet.
    // Keyed on the coordinates, so a poll returning the same stops is a no-op
    // and only a genuinely new/changed route costs a request.
    stopPathsByTrip: {
      immediate: true,
      handler(paths) {
        Object.keys(paths).forEach((tripId) => {
          const key = JSON.stringify(paths[tripId]);
          const cached = this.roadPaths[tripId];
          if (cached && cached.key === key) return;
          // Claim the key BEFORE awaiting so the next poll does not fire a
          // second request for the same route while this one is in flight.
          this.$set(this.roadPaths, tripId, {key, path: null});
          fleetRoadPath(paths[tripId]).then((road) => {
            const current = this.roadPaths[tripId];
            if (!road || !current || current.key !== key) return;
            this.$set(this.roadPaths, tripId, {key, path: road});
          }).catch(() => {
            // Directions unavailable — the straight skeleton stays.
          });
        });
      },
    },
  },
  methods: {
    fetchLive() {
      if (this.loading) return;
      this.loading = true;
      this.$http.get(`${FLEET_BASE}/reports/live-positions`)
          .then((res) => {
            this.trucks = (res.data && res.data.data) ? res.data.data : [];
            this.syncSelected();
            this.retargetTweens(this.positioned);
            // Fit ONLY when the set of visible trucks changes — never on a
            // routine poll. Re-fitting every 12s pinned the view to the whole
            // route (VTE→Bokeo is ~600 km), where a truck advances less than a
            // pixel per refresh and looks parked, and it stole the zoom back
            // the moment anyone zoomed in to actually watch it.
            if (this.selected) {
              // Following one truck: keep it centred as it drives, and leave
              // the zoom alone so the operator can pick their own.
              this.center = {lat: Number(this.selected.lat), lng: Number(this.selected.lng)};

              return;
            }
            const signature = this.mappable.map((t) => t.trip_id).sort().join(',');
            if (signature !== this.fittedSignature) {
              this.fittedSignature = signature;
              this.fitToTrucks();
            }
          })
          .catch(() => {
            // old backend / no access — leave the list empty, no error spam
            this.trucks = [];
            this.selected = null;
            this.infoTrip = null;
          })
          .finally(() => {
            this.loading = false;
          });
    },
    // Manifest summary block, tolerant of an old backend that omits it.
    mf(t) {
      const m = (t && t.manifest) || {};
      return {
        received: Number(m.received) || 0,
        unloaded: Number(m.unloaded) || 0,
        on_board: Number(m.on_board) || 0,
        off_route: Number(m.off_route) || 0,
        off_route_on_board: Number(m.off_route_on_board) || 0,
      };
    },
    dirLabel(t) {
      return fleetDirection(t.direction).label;
    },
    dirIcon(t) {
      return fleetDirection(t.direction).icon;
    },
    dirClass(t) {
      return t.direction === 'return' ? 'dir-return' : 'dir-outbound';
    },
    // Red when reporting, grey when contact dropped, POINTED the way the truck
    // is driving, and carrying its plate. A stationary fix often reports no
    // heading, so we hold the last one seen rather than snapping it to north.
    truckIcon(t) {
      const color = t.stale ? '#9aa0a6' : '#e53935';
      let heading = Number(t.heading_deg);
      if (t.heading_deg === null || t.heading_deg === undefined || isNaN(heading)) {
        heading = this.lastHeadings[t.trip_id];
      } else {
        this.lastHeadings[t.trip_id] = heading;
      }
      const bucket = (heading === null || heading === undefined || isNaN(heading))
          ? 'none' : Math.round(heading / 5) * 5;
      const plate = t.truck_plate || '';
      const key = `${color}:${bucket}:${plate}`;
      if (!this.truckIcons[key]) {
        this.truckIcons[key] = fleetTruckIcon(color, bucket === 'none' ? null : bucket, plate);
      }

      return this.truckIcons[key];
    },
    // The plate, rendered as a DOM label so it uses the app's Lao font — text
    // baked into the icon SVG cannot reach the page's webfonts. The pill behind
    // it is drawn by truckIcon().
    truckLabel(t) {
      return fleetTruckLabel(t.truck_plate);
    },
    // Green = the stop the truck is heading for, grey = already departed or
    // skipped, blue = still ahead.
    stopIcon(s) {
      if (s.is_next) return this.nextStopIcon;
      if (s.status === 'departed' || s.status === 'skipped') return this.doneStopIcon;

      return this.aheadStopIcon;
    },
    truckTitle(t) {
      return `${t.truck_plate || ''} · ${t.route_name || ''} ${this.$t('fleetMaps.round_n', {n: t.round_number})}`;
    },
    lastSeen(t) {
      const s = Number(t.seconds_since) || 0;
      if (s < 60) return this.$t('fleetMaps.seconds_ago', {n: s});
      return moment().subtract(s, 'seconds').fromNow();
    },
    // Selecting a truck FOLLOWS it: centre on it and zoom in close enough that
    // its movement is visible. Fitting to its route instead would keep the
    // whole-country zoom, which is what made a moving truck look parked.
    select(t) {
      this.selected = t;
      this.infoTrip = t;
      this.zoom = this.followZoom;
      this.focus(t);
    },
    // List crosshair: pick this truck (map narrows to it), click again to release.
    toggleSelect(t) {
      if (this.selected && this.selected.trip_id === t.trip_id) {
        this.clearSelection();

        return;
      }
      this.select(t);
    },
    // Releasing the truck DOES take the bubble with it — the map is no longer
    // showing that truck alone, so a popup pinned to it would be orphaned.
    clearSelection() {
      this.selected = null;
      this.infoTrip = null;
      this.zoom = 7;
      this.fittedSignature = '';   // force one re-fit back to the whole fleet
    },
    // Google renders the InfoWindow outside the app's DOM, so a <router-link>
    // inside it does not always get the router's click handling — navigate
    // programmatically instead of relying on the anchor.
    goToTrip(t) {
      if (!t || !t.trip_id) return;
      this.$router.push({name: 'fleet.trips.detail', params: {id: t.trip_id}});
    },
    // Re-point the followed truck and the open bubble at the freshest rows
    // after a poll. Both are re-resolved: a truck that closes its trip drops
    // out of the response and must not leave a stale popup behind.
    syncSelected() {
      if (this.selected) {
        const match = this.trucks.find((t) => t.trip_id === this.selected.trip_id);
        this.selected = match || null;
      }
      if (this.infoTrip) {
        const match = this.trucks.find((t) => t.trip_id === this.infoTrip.trip_id);
        this.infoTrip = match || null;
      }
    },
    focus(t) {
      if (Number(t.lat) && Number(t.lng)) {
        this.center = {lat: Number(t.lat), lng: Number(t.lng)};
      }
    },
    // Where a truck is DRAWN right now — the tweened position while it is
    // gliding, the reported one otherwise.
    truckPos(t) {
      const p = this.animPos[t.trip_id];

      return p ? {lat: p.lat, lng: p.lng} : {lat: t.lat, lng: t.lng};
    },
    // Start (or retarget) a glide for every truck in this poll. Retargeting
    // mid-glide is deliberate: a fix landing while the previous one is still
    // animating continues from where the marker actually is, so it never
    // stutters backwards.
    retargetTweens(trucks) {
      const now = (window.performance && window.performance.now)
          ? window.performance.now() : Date.now();
      const seen = {};

      trucks.forEach((t) => {
        seen[t.trip_id] = true;
        const to = {lat: Number(t.lat), lng: Number(t.lng)};
        const from = this.animPos[t.trip_id];
        if (!from) {
          this.$set(this.animPos, t.trip_id, to);   // first sight — no glide

          return;
        }
        if (from.lat === to.lat && from.lng === to.lng) {
          delete this.tweens[t.trip_id];            // parked — nothing to do

          return;
        }
        // Rough km; only used to decide glide-vs-snap, so a flat approximation
        // is plenty and avoids a haversine per truck per poll.
        const dLat = (to.lat - from.lat) * 111;
        const dLng = (to.lng - from.lng) * 111 * Math.cos(to.lat * Math.PI / 180);
        if (Math.sqrt((dLat * dLat) + (dLng * dLng)) > this.animMaxGlideKm) {
          this.$set(this.animPos, t.trip_id, to);   // teleport — snap, do not slide
          delete this.tweens[t.trip_id];

          return;
        }
        this.$set(this.tweens, t.trip_id, {from: {lat: from.lat, lng: from.lng}, to, start: now});
      });

      // Forget trucks that dropped out of the response.
      Object.keys(this.animPos).forEach((id) => {
        if (!seen[id]) {
          this.$delete(this.animPos, id);
          delete this.tweens[id];
        }
      });

      this.runAnimation();
    },
    runAnimation() {
      if (this.animRaf) return;   // a loop is already running
      const step = () => {
        const now = (window.performance && window.performance.now)
            ? window.performance.now() : Date.now();
        let active = false;

        Object.keys(this.tweens).forEach((id) => {
          const tw = this.tweens[id];
          const p = Math.min(1, (now - tw.start) / this.animDurationMs);
          // easeInOutQuad — a truck accelerates and settles, it does not
          // start and stop at full speed.
          const e = p < 0.5 ? (2 * p * p) : (1 - (Math.pow((-2 * p) + 2, 2) / 2));
          this.$set(this.animPos, id, {
            lat: tw.from.lat + ((tw.to.lat - tw.from.lat) * e),
            lng: tw.from.lng + ((tw.to.lng - tw.from.lng) * e),
          });
          if (p >= 1) delete this.tweens[id];
          else active = true;
        });

        this.animRaf = active ? window.requestAnimationFrame(step) : null;
      };
      this.animRaf = window.requestAnimationFrame(step);
    },
    // Mirror the operator's own zoom back into our state. Without this the
    // component keeps re-applying whatever `zoom` we last set, so a manual
    // zoom-in was undone by the next thing that touched the map — which is
    // exactly what made watching a moving truck impossible.
    onZoomChanged(z) {
      if (typeof z === 'number' && z > 0) this.zoom = z;
    },
    cycleMapMode() {
      const index = this.mapModes.findIndex((mode) => mode.id === this.mapTypeId);
      this.mapTypeId = this.mapModes[(index + 1) % this.mapModes.length].id;
    },
    fitToTrucks() {
      // Frame the trucks AND their stop points — fitting to the trucks alone
      // zoomed so far in on a single truck that its route was off-screen.
      const points = this.mappable.concat(this.stopPoints);
      if (!points.length) return;
      this.center = {lat: points[0].lat, lng: points[0].lng};
      if (points.length < 2 || !this.$refs.map || !this.$refs.map.$mapPromise) return;
      this.$refs.map.$mapPromise.then((map) => {
        if (!window.google || !window.google.maps) return;
        const bounds = new window.google.maps.LatLngBounds();
        points.forEach((point) => bounds.extend({lat: point.lat, lng: point.lng}));
        map.fitBounds(bounds, 60);
      }).catch(() => {
      });
    },
  },
  mounted() {
    this.fetchLive();
    this.timer = setInterval(this.fetchLive, this.intervalSec * 1000);
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
    // Leaving the page mid-glide would otherwise leave a rAF loop running
    // against a destroyed component.
    if (this.animRaf) {
      window.cancelAnimationFrame(this.animRaf);
      this.animRaf = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.live-map-page {
  padding: 12px;
  font-family: $font;

  .live-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    h4 {
      margin: 0;
    }

    .muted {
      color: #8a909a;
      font-size: 12px;
    }

    .reload-btn {
      font-family: $font;
      border: 1px solid #195BFF;
      color: #195BFF;
      background: white;
      border-radius: 4px;
      padding: 6px 14px;
      cursor: pointer;

      &:disabled {
        opacity: .5;
        cursor: default;
      }
    }
  }

  .live-body {
    display: flex;
    gap: 12px;

    .live-list {
      width: 300px;
      max-height: 540px;
      overflow-y: auto;
      flex-shrink: 0;

      .list-search {
        display: flex;
        align-items: center;
        gap: 6px;
        border: 1px solid #e6e8ec;
        border-radius: 6px;
        padding: 6px 9px;
        margin-bottom: 8px;
        background: #fafbfc;

        > i {
          color: #8a909a;
          font-size: 13px;
        }

        input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          font-family: $font;
          font-size: 13px;
          color: #2c3e50;

          &::placeholder {
            color: #b0b6bf;
          }
        }

        .clear {
          cursor: pointer;
          color: #8a909a;
        }
      }

      .empty {
        color: #8a909a;
        padding: 20px 8px;
        text-align: center;
      }

      .truck-row {
        display: block;
        text-align: left;
        text-decoration: none;
        color: inherit;
        border: 1px solid #e6e8ec;
        border-left: 4px solid #195BFF;
        border-radius: 6px;
        padding: 8px 10px;
        cursor: pointer;

        &.picked {
          background: #f2f6ff;
          border-color: #195BFF;
        }
        margin-bottom: 8px;
        cursor: pointer;
        transition: box-shadow .15s;

        &:hover {
          box-shadow: 0 2px 8px rgba(25, 91, 255, .12);
        }

        &.stale {
          border-left-color: #9aa0a6;
          background: #fafafa;
        }

        .row-top {
          display: flex;
          align-items: center;
          gap: 6px;

          .plate {
            font-weight: 700;
            flex: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .dir-chip {
            font-size: 10px;
            border-radius: 10px;
            padding: 1px 7px;
            white-space: nowrap;

            i {
              margin-right: 2px;
            }

            &.dir-outbound {
              color: #195BFF;
              background: rgba(25, 91, 255, .1);
            }

            &.dir-return {
              color: #0a9;
              background: rgba(0, 170, 153, .12);
            }
          }

          .badge {
            font-size: 11px;
            color: white;
            background: #2dcc70;
            border-radius: 10px;
            padding: 1px 8px;
            white-space: nowrap;

            &.stale {
              background: #9aa0a6;
            }
          }
        }

        .meta {
          font-size: 12px;
          color: #5a6472;
          margin-top: 3px;
        }

        .manifest {
          display: flex;
          flex-wrap: wrap;
          gap: 4px 10px;
          margin-top: 5px;
          font-size: 12px;
          color: #5a6472;

          b {
            font-weight: 700;
          }

          .in b {
            color: #2dcc70;
          }

          .out b {
            color: #e2445c;
          }

          .board b {
            color: #195BFF;
          }
        }

        .offroute {
          margin-top: 3px;
          font-size: 11px;
          color: #e8a10f;
        }

        .row-bottom {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 4px;

          .seen {
            font-size: 11px;
            color: #8a909a;
          }

          .detail-btn {
            font-family: $font;
            border: 1px solid #195BFF;
            background: white;
            color: #195BFF;
            cursor: pointer;
            padding: 3px 10px;
            border-radius: 12px;
            font-size: 11px;
            white-space: nowrap;

            i {
              margin-right: 3px;
            }

            &:hover {
              background: #195BFF;
              color: white;
            }
          }
        }
      }
    }

    .live-map {
      position: relative;
      flex: 1;

      .map-status {
        display: flex;
        align-items: center;
        margin-bottom: 6px;
        font-size: 12px;
        font-family: $font;
        color: grey;

        b {
          color: #333;
        }

        .ms-pick {
          color: #195BFF;

          b {
            color: #195BFF;
          }
        }

        .ms-clear {
          font-family: $font;
          margin-left: 8px;
          border: 1px solid #195BFF;
          background: white;
          color: #195BFF;
          border-radius: 10px;
          padding: 1px 9px;
          font-size: 11px;
          cursor: pointer;
        }
      }

      .map-mode-btn {
        position: absolute;
        top: 10px;
        left: 10px;
        z-index: 5;
        background: white;
        border: none;
        border-radius: 3px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, .3);
        padding: 8px 14px;
        font-size: 13px;
        font-family: $font;
        cursor: pointer;

        i {
          margin-right: 6px;
          color: #195BFF;
        }
      }
    }
  }

  @media (max-width: 900px) {
    .live-body {
      flex-direction: column;

      .live-list {
        width: 100%;
      }
    }
  }
}

// Google InfoWindow content is teleported out of the scoped tree — set the Lao
// font explicitly so ຮັບ/ລົງ/ເທິງລົດ render in the same face as the app.
.truck-info {
  font-family: $font;
  min-width: 160px;
  text-align: left;

  .ti-head {
    display: flex;
    align-items: center;
    gap: 6px;

    .ti-plate {
      font-weight: 700;
      font-size: 14px;
    }

    .ti-dir {
      font-size: 10px;
      border-radius: 10px;
      padding: 1px 7px;

      &.dir-outbound {
        color: #195BFF;
        background: rgba(25, 91, 255, .1);
      }

      &.dir-return {
        color: #0a9;
        background: rgba(0, 170, 153, .12);
      }
    }
  }

  .ti-meta {
    font-size: 12px;
    color: #5a6472;
    margin: 2px 0 4px;
  }

  .ti-lines {
    display: flex;
    flex-wrap: wrap;
    gap: 3px 10px;
    font-size: 12px;

    b {
      font-weight: 700;
    }

    .in {
      color: #2dcc70;
    }

    .out {
      color: #e2445c;
    }
  }

  .ti-offroute {
    font-size: 11px;
    color: #e8a10f;
    margin-top: 3px;
  }

  .ti-seen {
    margin-top: 4px;
    font-size: 11px;
    color: grey;
    font-family: $font;
  }

  .ti-detail-btn {
    display: block;
    width: 100%;
    margin-top: 8px;
    padding: 6px 10px;
    font-family: $font;
    font-size: 12px;
    font-weight: bold;
    color: white;
    background: #195BFF;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    i {
      margin-right: 4px;
    }

    &:hover {
      background: #0f47d1;
    }
  }
}
</style>
